import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { SnackbarContent } from 'notistack';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';

// Entrada portada de react-hot-toast: cae desde arriba con overshoot (la curva
// cubic-bezier(.21,1.02,.73,1) produce el rebote sutil). Solo transform/opacity.
const toastEnter = keyframes`
  from { transform: translate3d(0, -200%, 0) scale(0.6); opacity: 0.5; }
  to { transform: translate3d(0, 0, 0) scale(1); opacity: 1; }
`;

/**
 * Barra base de los toasts premium (éxito/error/warning/info comparten esta pieza y solo
 * cambia el icono). Superficie del tema en ambos modos; el mensaje llega ya traducido.
 */
function OtxToastBase(
  {
    id = undefined,
    message = '',
    action = null,
    icon = null,
    style = undefined,
    className = undefined
  },
  ref
) {
  // notistack permite `action` como nodo o como función (snackKey) => nodo (createSnack usa función).
  const resolvedAction = typeof action === 'function' ? action(id) : action;

  return (
    <SnackbarContent ref={ref} role="alert" style={style} className={className}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          width: '100%',
          maxWidth: 350,
          minWidth: 220,
          mx: 'auto',
          pl: 1.5,
          pr: 0.5,
          py: 0.75,
          borderRadius: '10px',
          bgcolor: 'surface.raised',
          color: 'text.primary',
          border: '1px solid',
          borderColor: 'hairline.main',
          boxShadow: (theme) => theme.palette.shadow.menu,
          animation: `${toastEnter} 0.35s cubic-bezier(0.21, 1.02, 0.73, 1)`,
          willChange: 'transform'
        }}>
        {icon}
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, lineHeight: 1.3, py: 0.75 }}>
          {message}
        </Typography>
        {resolvedAction}
      </Box>
    </SnackbarContent>
  );
}

OtxToastBase.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  message: PropTypes.node,
  action: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  icon: PropTypes.node,
  style: PropTypes.object,
  className: PropTypes.string
};

const OtxToast = forwardRef(OtxToastBase);

OtxToast.displayName = 'OtxToast';

export default OtxToast;
